import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import ErrorVerification from './errorVerification';
import { getOnboardingStatus } from '../../../redux/api';
import Loading from '../../../components/loading';
import { onboardingState } from '../../../redux/slices/detSlice';
import { selectProfile } from '../../../redux/slices/profileSlice';
import { selectSession } from '../../../redux/slices/sessionSlice';
import { setGuestVerificationLazyLoad } from '../../../redux/slices/sessionSlice';
import { Spinner } from 'react-bootstrap';
import { t } from 'i18next';

interface OnboardingModalProps {
	show: boolean;
	reservation: any;
	onCompleted: () => void;
	onRescan: () => void;
}

const OnboardingModal = ({
	show,
	reservation,
	onCompleted,
	onRescan,
}: OnboardingModalProps) => {
	const dispatch = useAppDispatch();
	const [statusMessage, setStatusMessage] = useState(
		'Checking onboarding status...'
	);
	const [loading, setLoading] = useState(true); // Start loading immediately when modal opens
	const [showRescanButton, setShowRescanButton] = useState(false);
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [isCheck, setIsCheck] = useState(true);

	const isPollingRef = useRef(false);
	const stopPollingRef = useRef(false);

	const { onboardingToken } = useAppSelector(onboardingState);
	const { currentProfile } = useAppSelector(selectProfile);
	const { token } = useAppSelector(selectSession);

	// useEffect(() => {
	// 	dispatch(setGuestVerificationLazyLoad(loading));
	// 	return () => {
	// 		dispatch(setGuestVerificationLazyLoad(false));
	// 	};
	// }, [loading, dispatch]);

	// Effect to initialize states when modal is shown
	useEffect(() => {
		if (show) {
			dispatch(setGuestVerificationLazyLoad(true));
			setLoading(true);
			setShowRescanButton(false);
			setStatusMessage('Checking onboarding status...');
			setErrorMsg(null);
			stopPollingRef.current = false;
		}
	}, [show]);

	useEffect(() => {
		if (errorMsg) {
			setLoading(false);
			dispatch(setGuestVerificationLazyLoad(false));
			stopPollingRef.current = true; // Stop polling when error occurs
		}
	}, [errorMsg]);

	useEffect(() => {
		if (!show || !token || !onboardingToken) return;
		stopPollingRef.current = false;

		const fetchStatus = async () => {
			if (
				isPollingRef.current ||
				stopPollingRef.current ||
				!token ||
				!onboardingToken
			)
				return;
			isPollingRef.current = true;
			try {
				// setLoading(true);
				const response = await getOnboardingStatus(
					token,
					onboardingToken,
					currentProfile?.profileID
				);
				const isCompleted = response.data.result;
				const status = response.data.status?.toLowerCase();
				const responseStatus = response.data.message;

				const isError =
					status === 'rejected' ||
					status?.includes('rejected') ||
					status === 'failed' ||
					status === 'pending' ||
					status === 'exist' ||
					(responseStatus && responseStatus.toLowerCase().includes('pending'));

				if (isError) {
					const msg =
						responseStatus ||
						'Onboarding was rejected. Please rescan onboarding.';
					setStatusMessage(msg);

					if (msg.toLowerCase().includes('document expiry')) {
						setErrorMsg(
							'Document expired. Please rescan onboarding with a valid document.'
						);
					} else if (msg.toLowerCase().includes('face recognition')) {
						setErrorMsg(
							'Face authentication failed. Please rescan onboarding.'
						);
					} else if (msg.toLowerCase().includes('pending')) {
						setErrorMsg(
							t(
								'Onboarding is pending. Please wait for 30 minutes, and retry again.'
							)
						);
					} else if (msg.toLowerCase().includes('document check')) {
						setErrorMsg(t('Document scan failed. Please rescan onboarding.'));
					} else if (
						status === 'exist' ||
						msg.toLowerCase().includes('exist')
					) {
						setErrorMsg('Profile already exists. Please rescan again.');
					} else {
						setErrorMsg(msg || 'Onboarding failed. Please rescan onboarding.');
					}

					setShowRescanButton(true);
					stopPollingRef.current = true;
					setLoading(false);
					dispatch(setGuestVerificationLazyLoad(false));
				} else if (isCompleted) {
					setStatusMessage('Onboarding completed!');
					stopPollingRef.current = true;
					setLoading(false);
					dispatch(setGuestVerificationLazyLoad(false));
					onCompleted();
				} else {
					setStatusMessage('Onboarding in progress...');
				}
			} catch (error: any) {
				console.error('Error checking onboarding status:', error);
				const msg = error?.message || 'Error checking status. Retrying...';
				setStatusMessage(msg);
				if (msg.toLowerCase().includes('face authentication')) {
					setErrorMsg(msg);
				} else {
					setErrorMsg('Onboarding failed. Please rescan onboarding.');
				}
				// Loading will be set to false by the useEffect when errorMsg is set
				//dispatch(setGuestVerificationLazyLoad(false));
			} finally {
				isPollingRef.current = false;
			}
		};

		const intervalId = setInterval(fetchStatus, 5000);
		// fetchStatus();

		return () => {
			clearInterval(intervalId);
			stopPollingRef.current = true; // extra safety
		};
	}, [
		show,
		dispatch,
		reservation?.PmsConfirmationNo,
		onCompleted,
		token,
		onboardingToken,
		currentProfile?.profileID,
	]);

	if (!show) return null;

	const errorTitle = errorMsg?.toLowerCase().includes('document expired')
		? 'Document Expired'
		: errorMsg?.toLowerCase().includes('face')
		? 'Face Authentication Failed'
		: 'Onboarding Failed';

	return (
		<div className="relative">
			{/* {loading && (
				<div className="fixed vh-100 inset-0 z-50 flex flex-col items-center justify-center bg-white bg-opacity-95 p-4">
					<Loading />
					<p className="text-base text-gray-700 mt-3 text-center">
						{statusMessage}
					</p>
				</div>
			)} */}

			<div className="flex items-center justify-center p-5">
				{errorMsg ? (
					<ErrorVerification
						title={errorTitle}
						msg={errorMsg}
						onTryAgain={onRescan}
						tryAgainLabel="Rescan Onboarding"
						showChecks={isCheck}
					/>
				) : (
					<div className="text-center max-w-sm w-full ">
						{/* <p className="text-base text-gray-600 mb-5 leading-relaxed">
							{statusMessage}
						</p> */}
						{showRescanButton && !loading && (
							<button
								onClick={onRescan}
								className="mt-8 px-10 py-3 text-base font-semibold rounded-lg bg-gradient-to-br from-indigo-600 to-purple-700 text-white transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
								Rescan Onboarding
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default OnboardingModal;
